import { useState, useMemo } from "react";

interface UseSearchOptions<T> {
  items: T[];
  searchFields: (keyof T)[];
}

interface UseSearchReturn<T> {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredItems: T[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedItem: T | undefined;
  setSelectedItem: (item: T | undefined) => void;
  clearSearch: () => void;
  selectItem: (item: T) => void;
}

/**
 * Hook customizado para funcionalidade de busca em selects/comboboxes
 * 
 * @param options - Configurações do hook
 * @returns Objeto com estados e funções para gerenciar a busca
 */
export function useSearch<T extends Record<string, any>>({
  items,
  searchFields,
}: UseSearchOptions<T>): UseSearchReturn<T> {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | undefined>();

  // Filtrar itens baseado no termo de busca
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return items;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return items.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(term);
        }
        if (typeof value === 'number') {
          return value.toString().includes(term);
        }
        return false;
      })
    );
  }, [items, searchTerm, searchFields]);

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedItem(undefined);
  };

  const selectItem = (item: T) => {
    setSelectedItem(item);
    setIsOpen(false);
    setSearchTerm("");
  };

  return {
    searchTerm,
    setSearchTerm,
    filteredItems,
    isOpen,
    setIsOpen,
    selectedItem,
    setSelectedItem,
    clearSearch,
    selectItem,
  };
} 